import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { emailService, websocketService, subscriptionService, syncService } from '@/services';
import type { BulkReadResult, EmailDeleteOutcome } from '@/services/emailService';
import type { 
  Email,
  EmailThread,
  EmailFolder,
  EmailFilter,
  EmailComposer,
  PaginationParams,
} from '@/types';
import { useUiStore } from './ui';

export const useEmailStore = defineStore('email', () => {
  const emails = ref<Email[]>([]);
  const currentEmail = ref<Email | null>(null);
  const currentThread = ref<EmailThread | null>(null);
  const folders = ref<EmailFolder[]>([]);
  const selectedFolder = ref<string>('inbox');
  
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const error = ref<string | null>(null);
  
  const currentPage = ref(1);
  const pageLimit = ref(50);
  const totalEmails = ref(0);
  const hasMore = ref(false);
  
  const filters = ref<EmailFilter>({});
  const searchQuery = ref('');
  const storage = ref<{ used: number; total: number; percentage: number }>({ used: 0, total: 50, percentage: 0 });

  const unreadCount = computed(() =>
    emails.value.filter(email => !email.isRead).length
  );

  const hasUnread = computed(() => unreadCount.value > 0);

  // Mapea las carpetas reales del proveedor (Graph/Gmail) a las carpetas
  // canónicas de la barra lateral, emparejando por displayName (EN/ES).
  const FOLDER_ALIASES: Record<string, string[]> = {
    inbox: ['inbox', 'bandeja de entrada'],
    sent: ['sent items', 'sent', 'elementos enviados', 'enviados'],
    spam: ['junk email', 'spam', 'correo no deseado', 'no deseado'],
    archived: ['archive', 'archivo'],
    trash: ['deleted items', 'trash', 'elementos eliminados', 'papelera'],
  };

  // Conteo real de no leídos por carpeta, desde folders (GET /emails/folders).
  // Si una carpeta no se puede emparejar o el proveedor no reporta el conteo
  // (p. ej. Gmail devuelve 0), la clave queda ausente y el badge se oculta.
  const unreadByFolder = computed<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const [key, aliases] of Object.entries(FOLDER_ALIASES)) {
      const folder = folders.value.find(fo =>
        aliases.includes((fo.displayName || '').trim().toLowerCase())
      );
      if (folder && typeof folder.unreadItemCount === 'number') {
        map[key] = folder.unreadItemCount;
      }
    }
    return map;
  });

  // Carpetas creadas por el usuario en su buzón; las del sistema ya tienen su
  // entrada fija en el panel.
  const customFolders = computed(() => folders.value.filter(fo => !fo.isSystem));

  const sortedEmails = computed(() => 
    [...emails.value].sort((a, b) => 
      new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()
    )
  );

  // Agrupar correos por conversación usando conversationId de Microsoft Graph
  const conversationGroups = computed(() => {
    const groups = new Map<string, Email[]>();
    
    emails.value.forEach(email => {
      // Usar conversationId de Microsoft Graph (único por conversación)
      const convId = email.conversationId || email.id;
      
      if (!groups.has(convId)) {
        groups.set(convId, []);
      }
      groups.get(convId)!.push(email);
    });
    
    // Ordenar correos dentro de cada conversación por fecha (más reciente primero)
    groups.forEach(emails => {
      emails.sort((a, b) => 
        new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()
      );
    });
    
    return groups;
  });

  // Obtener lista de conversaciones (último correo de cada conversación)
  const conversations = computed(() => {
    const convArray: Array<{ latestEmail: Email; emails: Email[]; count: number }> = [];
    
    conversationGroups.value.forEach((emails) => {
      if (emails.length > 0) {
        const latestEmail = emails[0]; // Primer correo (más reciente)
        convArray.push({
          latestEmail,
          emails,
          count: emails.length
        });
      }
    });
    
    // Ordenar conversaciones por fecha del último correo
    const sorted = convArray.sort((a, b) => 
      new Date(b.latestEmail.receivedDateTime).getTime() - 
      new Date(a.latestEmail.receivedDateTime).getTime()
    );
    
    const multiEmail = sorted.filter(c => c.count > 1);
    
    return sorted;
  });

  async function fetchEmails(params?: Partial<PaginationParams & EmailFilter>) {
    isLoading.value = true;
    error.value = null;

    try {
      // Construir parámetros de consulta
      const queryParams: any = {
        page: currentPage.value,
        limit: pageLimit.value,
        ...params,
      };

      // Si no se especifica folder en params, usar selectedFolder
      if (!params?.folder && selectedFolder.value) {
        queryParams.folder = selectedFolder.value;
      }
      

      // Agregar filtros adicionales (excepto folder que ya se manejó)
      const { folder: _, ...otherFilters } = filters.value;
      Object.assign(queryParams, otherFilters);

      const response = await emailService.getEmails(queryParams);

      if (response) {
        emails.value = response.emails;
        totalEmails.value = response.totalCount;
        // El backend señala que quedan páginas con nextLink; no manda hasMore. Leerlo
        // dejaba hasMore en undefined (nunca aparecía "cargar más") y currentPage en
        // undefined, con lo que la página siguiente se pedía como NaN.
        hasMore.value = !!response.nextLink;
        currentPage.value = queryParams.page;

        console.log('[email] fetchEmails ←', {
          folder: queryParams.folder,
          page: queryParams.page,
          recibidos: response.emails?.length,
          totalCount: response.totalCount,
          nextLink: response.nextLink ?? '(sin nextLink → no hay más páginas)',
          hasMore: hasMore.value,
        });
      }
    } catch (err: any) {
      error.value = err.message || 'Error al cargar correos';
      console.error('Error fetching emails:', err);
    } finally {
      isLoading.value = false;
    }
  }

  async function loadMoreEmails() {
    if (!hasMore.value || isLoadingMore.value) {
      console.warn('[email] loadMoreEmails ignorado', {
        hasMore: hasMore.value,
        isLoadingMore: isLoadingMore.value,
        currentPage: currentPage.value,
        selectedFolder: selectedFolder.value,
      });
      return;
    }

    isLoadingMore.value = true;

    try {
      // La carpeta activa manda sobre los filtros, que no la incluyen: mismo criterio
      // que fetchEmails. Sin esto, "cargar más" en Enviados, Spam, Archivados o
      // Papelera pedía la carpeta por defecto del backend y mezclaba otra bandeja.
      const { folder: _, ...otherFilters } = filters.value;

      const nextPage = currentPage.value + 1;

      console.log('[email] loadMoreEmails →', { page: nextPage, folder: selectedFolder.value });

      const response = await emailService.getEmails({
        page: nextPage,
        limit: pageLimit.value,
        ...otherFilters,
        ...(selectedFolder.value ? { folder: selectedFolder.value } : {}),
      });

      if (response) {
        emails.value = [...emails.value, ...response.emails];
        hasMore.value = !!response.nextLink;
        currentPage.value = nextPage;
      }
    } catch (err: any) {
      error.value = err.message || 'Error al cargar más correos';
    } finally {
      isLoadingMore.value = false;
    }
  }

  async function fetchEmailById(id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const email = await emailService.getEmailById(id);
      
      if (email) {
        currentEmail.value = email;
        
        const index = emails.value.findIndex(e => e.id === id);
        if (index !== -1) {
          emails.value[index] = email;
        } else {
          emails.value.push(email);
        }
      } else {
        console.error('No email returned from API');
      }
    } catch (err: any) {
      error.value = err.message || 'Error al cargar correo';
      console.error('Error fetching email:', err);
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchThread(conversationId: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const thread = await emailService.getEmailThread(conversationId);
      
      if (thread) {
        currentThread.value = thread;
      }
    } catch (err: any) {
      error.value = err.message || 'Error al cargar conversación';
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * El mensaje de error (404/413/502 de un correo adjunto, por ejemplo) viaja
   * en el resultado de la llamada, no en `error` (singleton del store): con
   * varios compositores enviando a la vez, el de uno se vería en el aviso de
   * otro. `EmailComposer.vue` lee `errorMessage`, nunca `emailStore.error`.
   */
  async function sendEmail(emailData: EmailComposer): Promise<{ ok: boolean; errorMessage?: string }> {
    isLoading.value = true;

    try {
      const { email, errorMessage } = await emailService.sendEmail(emailData);

      // No agregar el correo al inbox ya que el backend no devuelve el correo completo
      // El correo aparecerá en "Enviados" cuando se consulte esa carpeta
      return { ok: !!email, errorMessage };
    } catch (err: any) {
      console.error('Error sending email:', err);
      return { ok: false, errorMessage: err.message };
    } finally {
      isLoading.value = false;
    }
  }

  async function replyToEmail(
    emailId: string,
    replyData: Partial<EmailComposer>,
    replyAll: boolean = false
  ): Promise<{ ok: boolean; errorMessage?: string }> {
    isLoading.value = true;

    try {
      const { email, errorMessage } = await emailService.replyToEmail(emailId, replyData, replyAll);
      return { ok: !!email, errorMessage };
    } catch (err: any) {
      console.error('Error replying to email:', err);
      return { ok: false, errorMessage: err.message };
    } finally {
      isLoading.value = false;
    }
  }

  async function forwardEmail(emailId: string, forwardData: Partial<EmailComposer>) {
    isLoading.value = true;

    try {
      // El backend devuelve el resultado del envío, no el correo reenviado: la
      // copia enviada la trae el webhook al sincronizar, no se inserta a mano.
      return await emailService.forwardEmail(emailId, forwardData);
    } catch (err: any) {
      return { ok: false, skippedAttachments: [], errorMessage: err.message };
    } finally {
      isLoading.value = false;
    }
  }

  async function markAsRead(emailId: string, isRead: boolean = true) {
    try {
      const success = await emailService.markAsRead(emailId, isRead);
      
      if (success) {
        // Forzar reactividad usando splice para reemplazar el elemento
        const index = emails.value.findIndex(e => e.id === emailId);
        if (index !== -1) {
          const email = emails.value[index];
          const updatedEmail = { ...email, isRead } as Email;
          emails.value.splice(index, 1, updatedEmail);
        } else {
          console.warn(`Email ${emailId} not found in emails array`);
        }
        
        if (currentEmail.value?.id === emailId) {
          currentEmail.value = { ...currentEmail.value, isRead } as Email;
        }

        // Refrescar conteos reales de no leídos por carpeta (best-effort).
        // Agrupado: en una operación masiva esto era un GET /folders por correo.
        scheduleFoldersRefresh();
      } else {
        console.error(`markAsRead failed for email ${emailId}`);
      }
      
      return success;
    } catch (err: any) {
      console.error('Error in markAsRead:', err);
      error.value = err.message || 'Error al marcar correo';
      return false;
    }
  }

  /**
   * Marca varios correos de una vez con UNA petición al backend.
   *
   * Es lo que debe usar cualquier acción sobre una selección: hacerlo con N
   * llamadas a `markAsRead` agotaba el limitador y tumbaba de paso la carga de
   * la bandeja. Devuelve también los que fallaron (p. ej. un correo que ya no
   * está en el buzón) para poder avisarlo en vez de darlo por hecho.
   */
  async function markManyAsRead(emailIds: string[], isRead: boolean = true): Promise<BulkReadResult> {
    if (!emailIds.length) return { updated: 0, succeeded: [], failed: [] };

    try {
      const result = await emailService.markManyAsRead(emailIds, isRead);

      if (result.succeeded.length) {
        const done = new Set(result.succeeded);
        emails.value = emails.value.map(e => (done.has(e.id) ? ({ ...e, isRead } as Email) : e));

        if (currentEmail.value && done.has(currentEmail.value.id)) {
          currentEmail.value = { ...currentEmail.value, isRead } as Email;
        }

        scheduleFoldersRefresh();
      }

      return result;
    } catch (err: any) {
      console.error('Error in markManyAsRead:', err);
      error.value = err.message || 'Error al marcar correos';
      return {
        updated: 0,
        succeeded: [],
        failed: emailIds.map(id => ({ id, reason: err.message || 'error desconocido' })),
      };
    }
  }

  /**
   * Marca TODOS los mensajes de un hilo como leídos/no leídos.
   * Marcar solo el último mensaje deja el hilo con correos UNREAD y la
   * bandeja lo sigue mostrando como no leído aunque ya se haya abierto.
   */
  async function markThreadAsRead(threadId: string, isRead: boolean = true) {
    try {
      const success = await emailService.markThreadAsRead(threadId, isRead);

      if (success) {
        emails.value = emails.value.map(e =>
          e.conversationId === threadId ? ({ ...e, isRead } as Email) : e
        );

        if (currentEmail.value?.conversationId === threadId) {
          currentEmail.value = { ...currentEmail.value, isRead } as Email;
        }

        // Refrescar conteos reales de no leídos por carpeta (best-effort).
        // Agrupado: en una operación masiva esto era un GET /folders por correo.
        scheduleFoldersRefresh();
      } else {
        console.error(`markThreadAsRead failed for thread ${threadId}`);
      }

      return success;
    } catch (err: any) {
      console.error('Error in markThreadAsRead:', err);
      error.value = err.message || 'Error al marcar hilo';
      return false;
    }
  }

  async function deleteEmail(emailId: string): Promise<EmailDeleteOutcome> {
    try {
      const outcome = await emailService.deleteEmail(emailId);
      
      if (outcome === 'deleted') {
        emails.value = emails.value.filter(e => e.id !== emailId);

        if (currentEmail.value?.id === emailId) {
          currentEmail.value = null;
        }

        // Refrescar conteos reales de no leídos por carpeta (best-effort).
        // Agrupado: en una operación masiva esto era un GET /folders por correo.
        scheduleFoldersRefresh();
      }

      return outcome;
    } catch (err: any) {
      error.value = err.message || 'Error al eliminar correo';
      return 'failed';
    }
  }

  async function permanentDeleteEmail(emailId: string): Promise<EmailDeleteOutcome> {
    try {
      const outcome = await emailService.permanentDeleteEmail(emailId);
      
      if (outcome === 'deleted') {
        emails.value = emails.value.filter(e => e.id !== emailId);
        
        if (currentEmail.value?.id === emailId) {
          currentEmail.value = null;
        }
      }
      
      return outcome;
    } catch (err: any) {
      error.value = err.message || 'Error al eliminar permanentemente el correo';
      return 'failed';
    }
  }

  async function toggleFlag(emailId: string, isFlagged: boolean) {
    try {
      const success = await emailService.toggleFlag(emailId, isFlagged);
      
      if (success) {
        const email = emails.value.find(e => e.id === emailId);
        if (email) {
          if (!email.flag) {
            email.flag = { flagStatus: 'notFlagged' };
          }
          email.flag.flagStatus = isFlagged ? 'flagged' : 'notFlagged';
        }
        
        if (currentEmail.value?.id === emailId) {
          if (!currentEmail.value.flag) {
            currentEmail.value.flag = { flagStatus: 'notFlagged' };
          }
          currentEmail.value.flag.flagStatus = isFlagged ? 'flagged' : 'notFlagged';
        }
      }
      
      return success;
    } catch (err: any) {
      error.value = err.message || 'Error al marcar correo';
      return false;
    }
  }

  async function archiveEmail(emailId: string) {
    try {
      const success = await emailService.moveEmail(emailId, 'archive');
      
      if (success) {
        emails.value = emails.value.filter(e => e.id !== emailId);

        if (currentEmail.value?.id === emailId) {
          currentEmail.value = null;
        }

        // Refrescar conteos reales de no leídos por carpeta (best-effort).
        // Agrupado: en una operación masiva esto era un GET /folders por correo.
        scheduleFoldersRefresh();
      }

      return success;
    } catch (err: any) {
      error.value = err.message || 'Error al archivar correo';
      return false;
    }
  }

  async function restoreEmail(emailId: string, folder = 'inbox') {
    try {
      const success = await emailService.restoreEmail(emailId, folder);

      if (success) {
        emails.value = emails.value.filter(e => e.id !== emailId);
        if (currentEmail.value?.id === emailId) currentEmail.value = null;
        scheduleFoldersRefresh();
      }

      return success;
    } catch (err: any) {
      error.value = err.message || 'Error al recuperar correo';
      return false;
    }
  }

  /** `sourceFolder`: carpeta desde la que se mueve (la vista actual), para que Gmail quite esa etiqueta. */
  async function moveToFolder(emailId: string, folder: string, sourceFolder?: string) {
    try {
      const folderName = folders.value.find(fo => fo.id === folder)?.displayName;
      const success = await emailService.moveEmail(emailId, folder, undefined, folderName, sourceFolder);
      
      if (success) {
        emails.value = emails.value.filter(e => e.id !== emailId);

        if (currentEmail.value?.id === emailId) {
          currentEmail.value = null;
        }

        // Refrescar conteos reales de no leídos por carpeta (best-effort).
        // Agrupado: en una operación masiva esto era un GET /folders por correo.
        scheduleFoldersRefresh();
      }

      return success;
    } catch (err: any) {
      error.value = err.message || 'Error al mover correo';
      return false;
    }
  }

  async function fetchFolders() {
    try {

      // Modo producción: usar API real
      const folderList = await emailService.getFolders();
      folders.value = folderList;
    } catch (err: any) {
      error.value = err.message || 'Error al cargar carpetas';
    }
  }

  async function createFolder(displayName: string, parentFolderId?: string) {
    try {
      const folder = await emailService.createFolder(displayName, parentFolderId);
      if (folder) folders.value = [...folders.value, folder];
      return folder;
    } catch (err: any) {
      error.value = err.message || 'Error al crear carpeta';
      return null;
    }
  }

  async function renameFolder(folderId: string, displayName: string) {
    try {
      const updated = await emailService.renameFolder(folderId, displayName);
      if (updated) {
        folders.value = folders.value.map((f) => (f.id === folderId ? { ...f, displayName } : f));
      }
      return updated;
    } catch (err: any) {
      error.value = err.message || 'Error al renombrar carpeta';
      return null;
    }
  }

  async function deleteFolder(folderId: string) {
    try {
      const ok = await emailService.deleteFolder(folderId);
      if (ok) folders.value = folders.value.filter((f) => f.id !== folderId);
      return ok;
    } catch (err: any) {
      error.value = err.message || 'Error al eliminar carpeta';
      return false;
    }
  }

  /**
   * Refresco de conteos por carpeta agrupado en uno solo.
   *
   * Marcar, borrar o archivar llamaba a `fetchFolders()` tras CADA correo: una
   * operación sobre 900 correos disparaba 900 GET /emails/folders además de los
   * 900 PATCH, y era ese GET el que se quedaba sin cuota y dejaba la bandeja
   * congelada. Con la espera, una ráfaga se resuelve con una sola llamada.
   */
  let foldersRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleFoldersRefresh(delayMs: number = 800) {
    if (foldersRefreshTimer) clearTimeout(foldersRefreshTimer);
    foldersRefreshTimer = setTimeout(() => {
      foldersRefreshTimer = null;
      fetchFolders();
    }, delayMs);
  }

  async function fetchStorage() {
    try {
      const storageData = await emailService.getMailboxStorage();
      storage.value = storageData;
    } catch (err: any) {
      console.error('Error al cargar almacenamiento:', err);
    }
  }

  async function searchEmails(query: string) {
    isLoading.value = true;
    error.value = null;
    searchQuery.value = query;

    try {
      const response = await emailService.searchEmails(query, {
        page: 1,
        limit: pageLimit.value,
      });

      if (response) {
        emails.value = response.emails;
        totalEmails.value = response.totalCount;
        hasMore.value = !!response.nextLink;
        currentPage.value = 1;
      }
    } catch (err: any) {
      error.value = err.message || 'Error al buscar correos';
    } finally {
      isLoading.value = false;
    }
  }

  function setFilters(newFilters: EmailFilter) {
    filters.value = newFilters;
    currentPage.value = 1;
    fetchEmails();
  }

  function clearFilters() {
    filters.value = {};
    searchQuery.value = '';
    currentPage.value = 1;
    fetchEmails();
  }

  function setCurrentEmail(email: Email | null) {
    currentEmail.value = email;
  }

  function selectFolder(folderId: string) {
    selectedFolder.value = folderId;
    currentPage.value = 1;
    // Limpiar filtros anteriores y pasar folder como parámetro
    fetchEmails({ folder: folderId });
  }

  // HomeView llama setupRealtimeListeners en cada montaje del buzón y los
  // handlers nunca se quitaban: cada visita sumaba tres más al WebSocket.
  let realtimeListenersReady = false;

  function setupRealtimeListeners() {
    if (realtimeListenersReady) return;
    realtimeListenersReady = true;

    websocketService.on('new_email', (message) => {
      
      // El correo real está en message.data.data debido al doble anidamiento del WebSocket
      const emailData = (message.data as any)?.data || message.data;
      
      const newEmail = emailData as Email;
      
      // Validar que el correo tenga los datos mínimos necesarios
      if (!newEmail.from || !newEmail.from.email) {
        console.error('Email received without valid from field:', newEmail);
        return;
      }
      
      // Asegurar que from.name tenga un valor
      if (!newEmail.from.name) {
        newEmail.from.name = newEmail.from.email;
      }
      
      // Verificar si el correo ya existe para evitar duplicados
      const emailExists = emails.value.some(e => e.id === newEmail.id);
      if (emailExists) {
        return;
      }
      
      emails.value.unshift(newEmail);
      totalEmails.value++;
      
      // Mostrar notificación profesional de nuevo email
      const uiStore = useUiStore();
      uiStore.showEmailNotification(
        newEmail.from.name || newEmail.from.email,
        newEmail.subject || '(Sin asunto)'
      );
    });

    websocketService.on('email_updated', (message) => {
      const emailData = (message.data as any)?.data || message.data;
      const updatedEmail = emailData as Email;
      const index = emails.value.findIndex(e => e.id === updatedEmail.id);
      
      if (index !== -1) {
        emails.value[index] = updatedEmail;
      }
      
      if (currentEmail.value?.id === updatedEmail.id) {
        currentEmail.value = updatedEmail;
      }
    });

    websocketService.on('email_deleted', (message) => {
      const emailData = (message.data as any)?.data || message.data;
      const deletedId = emailData.id;
      emails.value = emails.value.filter(e => e.id !== deletedId);
      
      if (currentEmail.value?.id === deletedId) {
        currentEmail.value = null;
      }
      
      totalEmails.value--;
    });
  }

  async function setupEmailSync(companyId: string) {
    try {
      const subscriptionResult = await subscriptionService.createSubscription(companyId);

      const syncResult = await syncService.initialSync(companyId, 50);

      return { subscription: subscriptionResult, sync: syncResult };
    } catch (err) {
      console.error('❌ Email sync setup failed:', err);
      throw err;
    }
  }

  function clearError() {
    error.value = null;
  }

  function clearEmails() {
    emails.value = [];
    currentEmail.value = null;
    currentThread.value = null;
    totalEmails.value = 0;
    hasMore.value = false;
    currentPage.value = 1;
  }

  return {
    emails,
    sortedEmails,
    conversationGroups,
    conversations,
    currentEmail,
    folders,
    selectedFolder,
    isLoading,
    isLoadingMore,
    error,
    totalEmails,
    currentPage,
    hasMore,
    unreadCount,
    unreadByFolder,
    customFolders,
    fetchEmails,
    loadMoreEmails,
    fetchEmailById,
    sendEmail,
    replyToEmail,
    forwardEmail,
    markAsRead,
    markManyAsRead,
    markThreadAsRead,
    deleteEmail,
    permanentDeleteEmail,
    toggleFlag,
    archiveEmail,
    moveToFolder,
    restoreEmail,
    fetchFolders,
    createFolder,
    renameFolder,
    deleteFolder,
    fetchStorage,
    setCurrentEmail,
    selectFolder,
    setupRealtimeListeners,
    setupEmailSync,
    clearError,
    clearEmails,
    storage,
  };
});
